import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Sparkles, LayoutTemplate, Wand2, ArrowRight, Scissors, Droplets, FileCheck2, Crop, Smartphone, Printer } from "lucide-react";

const PILLARS = [
  { Icon: Sparkles, title: "Logo-Auffrischung", text: "Lade dein Logo hoch – Robbie frischt es mit KI auf und zeigt dir 3 moderne Varianten. Vorschau kostenlos, du zahlst erst, wenn dir eine gefällt." },
  { Icon: LayoutTemplate, title: "Master-Layout", text: "Dein Firmen-Design einmal festgelegt – Farben, Schrift, Logo. Jede Grafik sitzt danach automatisch in deiner Corporate Identity. Kein Grafiker nötig." },
  { Icon: Wand2, title: "Grafik- & Fotostudio", text: "PSD, PDF und Fotos direkt im Browser bearbeiten: Ebenen verschieben, Texte ändern, Hintergrund freistellen – und druckfertig exportieren." },
];

const STEPS = [
  { n: "1", title: "Vorlage wählen", text: "Hunderte fertige Vorlagen auf Deutsch – Social Post, Speisekarte, Flyer oder Plakat. Ein Tipp und los." },
  { n: "2", title: "Texte & Foto anpassen", text: "Schnapp ein Foto mit dem Handy, tippe deinen Text ein. Das Master-Layout richtet alles sauber aus." },
  { n: "3", title: "Druckfertig exportieren", text: "Mit einem Tipp als PDF mit 3 mm Beschnitt & Schnittmarken – direkt an die Druckerei oder den Bildschirm." },
];

const PRINT = [
  { Icon: Scissors, title: "3 mm Beschnitt", text: "Randlos drucken ohne weiße Kanten." },
  { Icon: Droplets, title: "CMYK-Farbraum", text: "Farben wie sie wirklich gedruckt werden." },
  { Icon: FileCheck2, title: "PDF/X-3", text: "Der Standard, den jede Druckerei akzeptiert." },
  { Icon: Crop, title: "Schnittmarken", text: "Automatisch gesetzt – nichts selbst messen." },
];

const FAQ = [
  { q: "Brauche ich Vorkenntnisse?", a: "Nein. Du wählst eine fertige Vorlage auf Deutsch, tauschst Text und Foto aus – das Master-Layout richtet alles sauber in deinem Firmen-Design aus. Wer mag, geht mit Ebenen tiefer rein, muss aber nicht." },
  { q: "Welche Formate bekomme ich?", a: "Social-Media-Posts und Stories, Flyer, Speisekarten und Plakate. Für den Druck exportierst du druckfertige PDFs mit 3 mm Beschnitt, Schnittmarken und CMYK – PSD- und Foto-Import inklusive." },
  { q: "Wie kündige ich?", a: "Die ersten 30 Tage sind kostenlos und ohne Zahlungsdaten. Danach 19 €/Monat, jederzeit zum Monatsende kündbar – mit einem Klick im Konto, ohne Kündigungsfrist." },
];

const CTA = "/kunden/registrieren";

export default function GrafikStudioPage() {
  return (
    <main className="bg-[#FBFAF5] text-[#111]" data-testid="grafik-studio-landing">
      <Seo title="Grafik-Studio – druckreife Grafiken vom Handy | DIMEDIAL Studio" description="Erstelle hochauflösende Social-Media-Grafiken, Flyer und druckfertige PDFs mit Beschnitt – direkt vom Handy. Logo-Auffrischung, Master-Layout und Grafik-Studio. 30 Tage kostenlos testen." path="/grafik-studio" />

      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-[#111] bg-[#FDD105]">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="gs-hero-badge"><Smartphone size={14} /> Vom Handy. In Druckqualität.</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl lg:text-6xl" data-testid="gs-hero-headline">Druckreife<br />Grafiken.<br /><span className="bg-[#111] px-2 text-[#FDD105]">Unterwegs.</span></h1>
            <p className="mt-5 max-w-md text-base font-medium leading-relaxed text-[#2b2b2b] sm:text-lg" data-testid="gs-hero-sub">Social-Media-Posts, Flyer und druckfertige PDFs mit Beschnitt – in Sekunden, direkt am Handy. Profi-Werkzeuge, die dir die Arbeit abnehmen.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={CTA} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#111] px-6 py-3 text-sm font-bold uppercase text-white shadow-[5px_5px_0_rgba(0,0,0,0.25)] transition-transform hover:-translate-y-0.5" data-testid="gs-hero-cta">Kostenlos testen <ArrowRight size={16} /></Link>
              <a href="#saeulen" className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-6 py-3 text-sm font-bold uppercase transition-transform hover:-translate-y-0.5" data-testid="gs-hero-more">Was geht alles?</a>
            </div>
            <p className="mt-3 text-xs font-semibold text-[#111]/70">30 Tage kostenlos · keine Zahlungsdaten · danach 19 €/Monat, monatlich kündbar</p>
          </div>
          <div className="relative mx-auto w-full max-w-sm">
            <div className="relative aspect-[2/3] rotate-1 overflow-hidden rounded-3xl border-2 border-[#111] bg-white shadow-[10px_10px_0_#111]">
              <picture>
                <source type="image/webp" srcSet="/img/nav/cv-grafik.webp" />
                <img src="/img/nav/cv-grafik.jpg" alt="Robbie im Grafik-Studio" className="absolute inset-0 h-full w-full object-cover" width="640" height="960" loading="eager" />
              </picture>
            </div>
            <div className="absolute -bottom-4 -left-3 w-24 -rotate-3 overflow-hidden rounded-2xl border-2 border-[#111] bg-white shadow-[6px_6px_0_#111] sm:w-28" data-testid="gs-hero-beispiel">
              <span className="absolute left-1.5 top-1.5 z-10 rounded-full bg-[#111] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.15em] text-white">Beispiel</span>
              <img src="/grafik-lp/hero_phone_de.jpg" alt="Grafik-Studio auf dem Smartphone" className="block h-auto w-full object-cover" width="848" height="1264" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Preis-/Test-Banner */}
      <section className="border-b-2 border-[#111] bg-[#111]" data-testid="gs-price-banner">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-6 text-center md:flex-row md:text-left">
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border-2 border-[#FDD105] bg-[#FDD105] font-display text-xl font-extrabold text-[#111]">30</span>
            <div>
              <p className="font-display text-xl font-extrabold uppercase leading-tight text-white sm:text-2xl" data-testid="gs-price-headline">30 Tage kostenlos testen</p>
              <p className="mt-0.5 text-sm text-white/70">Danach nur <span className="font-bold text-[#FDD105]">19 €/Monat</span> · keine Zahlungsdaten zum Start · monatlich kündbar</p>
            </div>
          </div>
          <Link to={CTA} className="inline-flex shrink-0 items-center gap-2 rounded-full border-2 border-[#FDD105] bg-[#FDD105] px-6 py-3 text-sm font-bold uppercase text-[#111] transition-transform hover:-translate-y-0.5" data-testid="gs-price-cta">Gratis starten <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-[#111] bg-[#FBFAF5]" data-testid="gs-faq">
        <div className="mx-auto max-w-3xl px-5 py-14 md:py-16">
          <h2 className="font-display text-2xl font-extrabold uppercase sm:text-3xl" data-testid="gs-faq-title">Häufige Fragen</h2>
          <div className="mt-6 space-y-4">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="rounded-2xl border-2 border-[#111] bg-white p-5 shadow-[4px_4px_0_#111]" data-testid={`gs-faq-item-${q}`}>
                <h3 className="font-display text-base font-bold uppercase leading-tight sm:text-lg">{q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#444]">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Säulen */}
      <section id="saeulen" className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="gs-pillars-title">Drei Profi-Werkzeuge,<br />ein Studio.</h2>
        <p className="mt-2 max-w-xl text-base text-[#5C574F]">Alles greift ineinander – und bleibt so einfach, dass du es vom Sofa aus bedienst.</p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {PILLARS.map(({ Icon, title, text }) => (
            <div key={title} className="flex flex-col gap-4 rounded-2xl border-2 border-[#111] bg-white p-6 shadow-[6px_6px_0_#111] transition-transform hover:-translate-y-1" data-testid={`gs-pillar-${title}`}>
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#FDD105] text-[#111]"><Icon size={24} /></span>
              <h3 className="font-display text-xl font-bold uppercase leading-tight">{title}</h3>
              <p className="text-sm leading-relaxed text-[#444]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="border-y-2 border-[#111] bg-white">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-20">
          <div>
            <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="gs-workflow-title">Unterwegs<br />in 60 Sekunden.</h2>
            <div className="mt-7 space-y-4">
              {STEPS.map(({ n, title, text }) => (
                <div key={n} className="flex gap-4" data-testid={`gs-step-${n}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-[#FDD105] font-display text-lg font-extrabold">{n}</span>
                  <div><h3 className="font-display text-lg font-bold uppercase leading-tight">{title}</h3><p className="mt-0.5 text-sm text-[#555]">{text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border-2 border-[#111] shadow-[10px_10px_0_#111]">
              <img src="/grafik-lp/print_desk.jpg" alt="Vom Handy zur gedruckten Grafik" className="h-full w-full object-cover" width="1264" height="848" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Druck-Qualität */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div className="order-2 md:order-1">
            <div className="mx-auto w-full max-w-[260px] -rotate-1 overflow-hidden rounded-[2rem] border-[3px] border-[#111] bg-[#111] p-2 shadow-[10px_10px_0_#111]" data-testid="gs-export-mockup">
              <div className="overflow-hidden rounded-[1.5rem] bg-[#1b1b1b]">
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                  <span className="text-[12px] font-bold uppercase tracking-wide text-white">Exportieren</span>
                  <span className="text-[10px] text-white/45">herbst-flyer.pdf</span>
                </div>
                <div className="mx-4 grid aspect-[4/3] place-items-center rounded-xl border border-dashed border-[#FDD105]/60 bg-[#FDD105]/10 text-center">
                  <div>
                    <p className="font-display text-base font-extrabold uppercase leading-none text-[#FDD105]">Herbst-<br />Aktion</p>
                    <p className="mt-1.5 text-[9px] uppercase tracking-wide text-white/55">A5 Flyer · druckfertig</p>
                  </div>
                </div>
                <div className="space-y-1.5 px-4 py-3">
                  {[["Auflösung", "300 dpi"], ["Farbe", "CMYK"], ["Beschnitt", "3 mm"], ["Dateigröße", "2,4 MB"]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-[11px]">
                      <span className="text-white/60">{k}</span>
                      <span className="rounded-md bg-white/10 px-2 py-0.5 font-semibold text-white">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 px-4 pb-4">
                  <span className="flex-1 rounded-lg border border-white/20 py-2 text-center text-[11px] font-bold text-white/80">Abbrechen</span>
                  <span className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#FDD105] py-2 text-center text-[11px] font-bold text-[#111]"><FileCheck2 size={13} /> Export</span>
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-xs font-bold uppercase"><Printer size={14} /> Profi-Druckstandard</span>
            <h2 className="mt-4 font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="gs-print-title">Druckerei-fertig.<br />Ohne Fachwissen.</h2>
            <p className="mt-2 max-w-md text-base text-[#5C574F]">Was Agenturen stundenlang einrichten, macht das Studio automatisch – du tippst nur auf „Exportieren".</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {PRINT.map(({ Icon, title, text }) => (
                <div key={title} className="flex items-start gap-3" data-testid={`gs-print-${title}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-[#111] bg-white text-[#111]"><Icon size={18} /></span>
                  <div><h3 className="font-display text-sm font-bold uppercase">{title}</h3><p className="text-xs text-[#555]">{text}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Abschluss-CTA */}
      <section className="border-t-2 border-[#111] bg-[#111]">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:py-20">
          <h2 className="font-display text-3xl font-extrabold uppercase text-white sm:text-4xl lg:text-5xl" data-testid="gs-cta-headline">Bereit für druckreife<br />Grafiken in Sekunden?</h2>
          <p className="mx-auto mt-4 max-w-lg text-base text-white/75">Teste das DIMEDIAL Grafik-Studio 30 Tage kostenlos – ohne Zahlungsdaten, danach 19 €/Monat und monatlich kündbar. Deine Sichtbarkeit (Social, Google, Tickets) läuft im selben Konto gleich mit.</p>
          <Link to={CTA} className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-8 py-4 text-base font-bold uppercase text-[#111] shadow-[6px_6px_0_rgba(253,209,5,0.3)] transition-transform hover:-translate-y-1" data-testid="gs-cta-button">Jetzt kostenlos testen <ArrowRight size={18} /></Link>
        </div>
      </section>
    </main>
  );
}
